// Recursively layers `override` onto `base`. Plain objects merge key by key,
// recursing into nested plain objects; anything else (arrays, strings,
// numbers, booleans, null) is a scalar as far as the merge is concerned, and
// `override`'s value wins outright — arrays are replaced, never concatenated
// or merged element-wise. Neither argument is mutated.
//
// Used to build one complete locale dictionary per region overlay (base
// language + its sparse overlay) at svelte-i18n registration time — see
// locales/regions/ and lib/i18n.ts/ssr/ssr.ts. svelte-i18n's own
// fallbackLocale is a single global fallback (always to "en"), not
// hierarchical de-CH -> de -> en chaining, so the merge must already be
// complete before it's registered.
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function deepMerge(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base }
  for (const key of Object.keys(override)) {
    const baseVal = base[key]
    const overrideVal = override[key]
    result[key] =
      isPlainObject(baseVal) && isPlainObject(overrideVal)
        ? deepMerge(baseVal, overrideVal)
        : overrideVal
  }
  return result
}
